/**
 * Git Clone Module
 * Uses isomorphic-git to clone repositories in the browser
 */

import git from 'isomorphic-git';
import http from 'isomorphic-git/http/web';
import LightningFS from '@isomorphic-git/lightning-fs';
import type { ProgressCallback } from '../../core/types';

// CORS proxy for git operations
const CORS_PROXY = 'https://cors.isomorphic-git.org';

// Initialize Lightning FS with IndexedDB backend
let fs: LightningFS | null = null;

export function getFS(): LightningFS {
  if (!fs) {
    fs = new LightningFS('webwebweb-fs');
  }
  return fs;
}

/**
 * Reset the filesystem (clear all cached data)
 */
export async function resetFS(): Promise<void> {
  // Delete IndexedDB database
  const dbName = 'webwebweb-fs';
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(dbName);
    request.onsuccess = () => {
      fs = null; // Reset the fs instance
      console.log('[www] Cleared filesystem cache');
      resolve();
    };
    request.onerror = () => {
      console.warn('[www] Failed to clear filesystem cache');
      resolve(); // Continue anyway
    };
    request.onblocked = () => {
      console.warn('[www] Database deletion blocked');
      resolve();
    };
  });
}

/**
 * Clone a GitHub repository
 */
export async function cloneRepo(
  owner: string,
  repo: string,
  token: string,
  options: {
    branch?: string;
    depth?: number;
    singleBranch?: boolean;
    onProgress?: ProgressCallback;
  } = {}
): Promise<string> {
  const fs = getFS();
  const dir = `/${owner}/${repo}`;

  // Clean up existing directory if present
  try {
    await fs.promises.rm(dir, { recursive: true });
  } catch {
    // Directory doesn't exist, that's fine
  }

  // Create parent directory first (LightningFS recursive mkdir can be finicky)
  try {
    await fs.promises.mkdir(`/${owner}`);
  } catch {
    // Parent already exists, that's fine
  }

  // Create repo directory
  try {
    await fs.promises.mkdir(dir);
  } catch {
    // Directory already exists, that's fine
  }

  const url = `https://github.com/${owner}/${repo}`;

  let lastPhase = '';
  let totalObjects = 0;

  console.log(`[www] Starting clone of ${url}`);

  // Send initial progress
  if (options.onProgress) {
    options.onProgress({
      phase: 'connecting',
      current: 0,
      total: 100,
      message: 'Connecting to GitHub...'
    });
  }

  // Note: cors.isomorphic-git.org doesn't forward auth headers
  // For private repos, we'd need a custom proxy
  // Token is included in headers for repos that support it
  await git.clone({
    fs,
    http,
    dir,
    corsProxy: CORS_PROXY,
    url,
    ref: options.branch,
    singleBranch: options.singleBranch ?? true,
    depth: options.depth,
    headers: {
      // Some CORS proxies forward these headers
      'Authorization': `token ${token}`
    },
    onAuth: () => ({
      username: token,
      password: 'x-oauth-basic'
    }),
    onAuthFailure: () => {
      console.log('[www] Auth failed - repo may be private or token invalid');
      // Return undefined to let it try without auth (for public repos)
      return undefined;
    },
    onProgress: (progress) => {
      const phase = progress.phase || 'cloning';
      if (phase !== lastPhase) {
        console.log(`[www] Clone phase: ${phase}`);
        lastPhase = phase;
      }
      if (progress.total) {
        totalObjects = progress.total;
      }
      if (options.onProgress) {
        options.onProgress({
          phase,
          current: progress.loaded || 0,
          total: totalObjects || 100,
          message: `${phase}: ${progress.loaded || 0}${totalObjects ? '/' + totalObjects : ''}`
        });
      }
    },
    onMessage: (message) => {
      console.log(`[www] Git: ${message}`);
    }
  });

  console.log(`[www] Clone complete: ${dir}`);

  return dir;
}

/**
 * Fetch more history for an existing repo
 */
export async function fetchMoreHistory(
  dir: string,
  token: string,
  options: {
    depth?: number;
    relative?: boolean;
    onProgress?: ProgressCallback;
  } = {}
): Promise<void> {
  const fs = getFS();

  await git.fetch({
    fs,
    http,
    dir,
    corsProxy: CORS_PROXY,
    depth: options.depth,
    relative: options.relative,
    onAuth: () => ({
      username: token,
      password: 'x-oauth-basic'
    }),
    onProgress: (progress) => {
      if (options.onProgress) {
        options.onProgress({
          phase: progress.phase || 'fetching',
          current: progress.loaded || 0,
          total: progress.total || 100,
          message: `Fetching: ${progress.loaded || 0}/${progress.total || '?'}`
        });
      }
    }
  });
}

/**
 * List all files in a cloned repository
 */
export async function listRepoFiles(dir: string): Promise<string[]> {
  const fs = getFS();
  const files: string[] = [];

  async function walkDir(currentDir: string, relativePath: string = '') {
    const entries = await fs.promises.readdir(currentDir);

    for (const entry of entries) {
      const fullPath = `${currentDir}/${entry}`;
      const relPath = relativePath ? `${relativePath}/${entry}` : entry;

      // Skip .git directory
      if (entry === '.git') continue;

      const stat = await fs.promises.stat(fullPath);

      if (stat.isDirectory()) {
        await walkDir(fullPath, relPath);
      } else {
        files.push(relPath);
      }
    }
  }

  await walkDir(dir);
  return files;
}

/**
 * Read a file from the cloned repository
 */
export async function readRepoFile(dir: string, filePath: string): Promise<string> {
  const fs = getFS();
  const fullPath = `${dir}/${filePath}`;
  const content = await fs.promises.readFile(fullPath, { encoding: 'utf8' });
  return content as string;
}

/**
 * Read all files and return as a Map
 */
export async function readAllRepoFiles(
  dir: string,
  filter?: (path: string) => boolean,
  onProgress?: ProgressCallback
): Promise<Map<string, string>> {
  const files = await listRepoFiles(dir);
  const filteredFiles = filter ? files.filter(filter) : files;
  const fileMap = new Map<string, string>();

  let current = 0;
  const total = filteredFiles.length;

  for (const filePath of filteredFiles) {
    current++;
    if (onProgress) {
      onProgress({
        phase: 'reading',
        current,
        total,
        message: `Reading ${filePath}`
      });
    }

    try {
      const content = await readRepoFile(dir, filePath);
      fileMap.set(filePath, content);
    } catch (err) {
      console.warn(`Failed to read ${filePath}:`, err);
    }
  }

  return fileMap;
}

/**
 * Check if a repository is already cloned
 */
export async function isRepoCloned(owner: string, repo: string): Promise<boolean> {
  const fs = getFS();
  const dir = `/${owner}/${repo}`;

  try {
    await fs.promises.stat(`${dir}/.git`);
    return true;
  } catch {
    return false;
  }
}

/**
 * Delete a cloned repository
 */
export async function deleteRepo(owner: string, repo: string): Promise<void> {
  const fs = getFS();
  const dir = `/${owner}/${repo}`;

  try {
    await fs.promises.rm(dir, { recursive: true });
  } catch {
    // Ignore errors
  }
}

/**
 * List all cloned repositories
 */
export async function listClonedRepos(): Promise<Array<{ owner: string; repo: string }>> {
  const fs = getFS();
  const repos: Array<{ owner: string; repo: string }> = [];

  try {
    const owners = await fs.promises.readdir('/');

    for (const owner of owners) {
      try {
        const ownerRepos = await fs.promises.readdir(`/${owner}`);
        for (const repo of ownerRepos) {
          // Check if it's a git repo
          try {
            await fs.promises.stat(`/${owner}/${repo}/.git`);
            repos.push({ owner, repo });
          } catch {
            // Not a git repo
          }
        }
      } catch {
        // Not a directory
      }
    }
  } catch {
    // Root doesn't exist yet
  }

  return repos;
}
